import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { issueTypeConfigsApi, type IssueType, type IssueTypeConfig } from "../../api/issues.api";

function IssueTypeRow({ config }: { config: IssueTypeConfig }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(config.label);
  const [description, setDescription] = useState(config.description ?? "");

  const update = useMutation({
    mutationFn: (input: { label: string; description: string | null }) =>
      issueTypeConfigsApi.update(config.type as IssueType, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["issue-type-configs"] });
      setEditing(false);
    },
  });

  const reset = useMutation({
    mutationFn: () => issueTypeConfigsApi.reset(config.type as IssueType),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["issue-type-configs"] }),
  });

  const toggleActive = useMutation({
    mutationFn: () => issueTypeConfigsApi.update(config.type as IssueType, { isActive: !config.isActive }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["issue-type-configs"] }),
  });

  function handleSave() {
    if (!label.trim()) return;
    update.mutate({ label: label.trim(), description: description.trim() || null });
  }

  function handleCancel() {
    setLabel(config.label);
    setDescription(config.description ?? "");
    setEditing(false);
  }

  return (
    <tr style={{ opacity: config.isActive ? 1 : 0.5 }}>
      <td>
        <code style={{ fontSize: 12, background: "rgba(255,255,255,0.06)", padding: "2px 6px", borderRadius: 4 }}>
          {config.type}
        </code>
      </td>
      <td>
        {editing ? (
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            style={{ width: "100%", minWidth: 140 }}
            autoFocus
          />
        ) : (
          config.label
        )}
      </td>
      <td>
        {editing ? (
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description"
            style={{ width: "100%", minWidth: 200 }}
          />
        ) : (
          <span className="muted">{config.description ?? "—"}</span>
        )}
      </td>
      <td>
        <span className={`badge ${config.isActive ? "success" : "muted"}`}>
          {config.isActive ? "Active" : "Hidden"}
        </span>
      </td>
      <td>
        {editing ? (
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="primary"
              onClick={handleSave}
              disabled={update.isPending || !label.trim()}
              style={{ fontSize: 13, padding: "4px 12px" }}
            >
              {update.isPending ? "Saving…" : "Save"}
            </button>
            <button onClick={handleCancel} style={{ fontSize: 13, padding: "4px 12px" }}>
              Cancel
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => setEditing(true)} style={{ fontSize: 13, padding: "4px 12px" }}>
              Edit
            </button>
            <button
              onClick={() => toggleActive.mutate()}
              disabled={toggleActive.isPending}
              style={{ fontSize: 13, padding: "4px 12px" }}
            >
              {config.isActive ? "Hide" : "Show"}
            </button>
            <button
              onClick={() => reset.mutate()}
              disabled={reset.isPending}
              style={{ fontSize: 13, padding: "4px 12px" }}
            >
              Reset
            </button>
          </div>
        )}
        {(update.isError || reset.isError) && (
          <p className="error-text" style={{ marginTop: 4, fontSize: 12 }}>Failed to save changes.</p>
        )}
      </td>
    </tr>
  );
}

export function AdminIssueTypes() {
  const { data: configs, isLoading } = useQuery({
    queryKey: ["issue-type-configs"],
    queryFn: issueTypeConfigsApi.list,
  });

  return (
    <>
      <div className="page-header">
        <h2>Issue Types</h2>
      </div>

      <p className="muted" style={{ marginBottom: 16 }}>
        Customize the labels and descriptions shown to users when submitting an issue. Hidden types won't appear in the submission form.
      </p>

      {isLoading && <p className="muted">Loading…</p>}

      {configs && (
        <div className="card table-scroll" style={{ padding: 0 }}>
          <table>
            <thead>
              <tr>
                <th>Type Key</th>
                <th>Label</th>
                <th>Description</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {configs.map((config) => (
                <IssueTypeRow key={config.type} config={config} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
